<template lang="pug">
v-row.mb-8
  v-col(cols="12")
    TextBody(:text="infoText")
    v-switch.mt-0(
      @change="setRemindersEnabled",
      v-model="remindersEnabled",
      inset
    )
      template(v-slot:label)
        TextBody(text="Erinnerung zur Anmeldung versenden", large)
  v-col(cols="12")
    Subtitle(text="Geplante Benachrichtigungen", :style="labelStyles")
    v-checkbox.mt-2(
      v-for="key in possibleReminders",
      :key="key",
      :input-value="isReminderChecked(key)",
      hide-details,
      :disabled="!isReminderActive(key)",
      @change="toggleReminder(key)",
      v-if="showReminder(key)"
    )
      template(v-slot:label)
        TextBody(
          :text="getReminderLabel(key)",
          large,
          :class="{ 'text--disabled': !isReminderActive(key) }"
        )
  v-col.mt-8(cols="12")
    v-alert(
      color="textColor",
      outlined,
      :style="alertStyles",
      v-if="canSendUrgent"
    )
      .d-flex.pa-4
        v-switch.mt-0(v-model="urgent", inset, :disabled="!!mailedAt")
        .pl-4(@click="urgent = !!mailedAt || !urgent")
          Subtitle(
            text="Dringende Benachrichtigung",
            :fontSize="1.1",
            :fontWeight="900"
          )
          TextBody(:text="t$('.send_urgent_message_text')")
          Subtitle(:text="alertTimeText", v-if="alertTimeText")
</template>

<script lang="ts">
import Header from "../UI/Header.vue"
import TextBody from "../UI/TextBody.vue"
import Subtitle from "../UI/Subtitle.vue"
import { EVENT_REMINDERS } from "../../constants/constants"
import moment from "moment"
import { useCanSendUrgentOnLevelOrClientFallback } from "features/common/permissions/usePermissionCheckHelper"
import { formatDateTime } from "helpers/dateTime"
import { PanelIdentifier } from "features/events/form/event-form.types"
import { computed, defineComponent, inject, WritableComputedRef } from "vue"
import EventFormService from "features/events/form/event-form.service"
import { EventForm } from "components/EventForm/EventForm.vue"
import store from "store"
import useTranslate from "features/common/translate/concerns/useTranslate"
import MemberService from "features/member/member.service"

const ReminderFields = defineComponent({
  name: "ReminderFields",
  components: { Header, TextBody, Subtitle },
  data() {
    return {
      remindersEnabled: true,
      infoText: `Hier kannst Du konfigurieren, ob und wann eine Erinnerung an diese Veranstaltung im Digest automatisch verschickt werden soll.`,
      canSendUrgent: false,
    }
  },
  computed: {
    possibleReminders() {
      return Object.keys(EVENT_REMINDERS)
    },
    labelStyles() {
      return {
        color: this.$vuetify.theme.themes.light.primary,
        fontWeight: 400,
      }
    },
    alertStyles() {
      return {
        borderColor: `${this.$vuetify.theme.themes.light.mediumGrey} !important`,
        fontFamily: "system-ui !important",
        fontSize: "1rem",
        color: `${this.$vuetify.theme.themes.light.textColor} !important`,
        marginBottom: "0px",
        width: "100%",
      }
    },
  },
  methods: {
    isReminderChecked(key) {
      return this.reminders?.length && this.reminders.includes(key)
    },
    isReminderActive(key) {
      if (this.isEditForm && key === "on_creation") return false

      return this.isReminderInFuture(key)
    },
    getReminderLabel(key) {
      let label = EVENT_REMINDERS[key].label
      if (!this.isReminderInFuture(key)) return label + " (abgelaufen)"

      return label
    },
    showReminder(key) {
      if (this.isEditForm) {
        return this.isReminderChecked(key) || this.isReminderInFuture(key)
      } else {
        return this.isReminderInFuture(key)
      }
    },
    toggleReminder(key, targetState = undefined) {
      targetState =
        targetState === undefined ? !this.isReminderChecked(key) : targetState

      if (targetState) {
        // Filter on possibleReminders to get ordered reminders
        this.reminders = this.possibleReminders.filter(
          (r) => this.isReminderChecked(r) || r === key
        )
      } else {
        this.reminders = this.reminders.filter((r) => r !== key)
      }

      // Check if all are unselected now
      this.remindersEnabled = !!this.possibleReminders.find((r) =>
        this.isReminderChecked(r)
      )
    },
    toggleFutureReminders(val) {
      this.possibleReminders
        .filter((r) => this.isReminderInFuture(r))
        .map((r) => this.toggleReminder(r, val))
    },
    formatDateTime(datetime) {
      return moment(datetime).format("dd. DD.MM.YYYY, HH:mm:ss")
    },
    setRemindersEnabled(val) {
      this.remindersEnabled = val
      this.toggleFutureReminders(val)
    },
  },
  watch: {
    startDate(dateNow: string, datePrev: string) {
      if (this.remindersEnabled) {
        const pastAvailableReminders = this.possibleReminders.filter(
          (r) => datePrev && this.isReminderInFuture(r, datePrev)
        )
        const nowAvailableReminders = this.possibleReminders.filter((r) =>
          this.isReminderInFuture(r, dateNow)
        )

        // Check reminders that become now available
        const newReminders = nowAvailableReminders.filter(
          (r) => !pastAvailableReminders.includes(r)
        )
        newReminders.map((r) => this.toggleReminder(r, true))

        // Uncheck reminders that became now unavailable
        const oldReminders = pastAvailableReminders.filter(
          (r) => !nowAvailableReminders.includes(r)
        )
        oldReminders.map((r) => this.toggleReminder(r, false))
      }
    },
    async group(val) {
      this.canSendUrgent = useCanSendUrgentOnLevelOrClientFallback(
        val?.level_id
      ).value
    },
  },
  mounted() {
    this.remindersEnabled = !this.isEditForm || this.reminders.length > 0
    this.canSendUrgent = this.canUrgentDigestOnClient
  },
  setup() {
    const { t$ } = useTranslate("components.event_form")

    const memberService: MemberService = inject<MemberService>("MemberService")

    const canUrgentDigestOnClient = memberService.canUrgentDigestOnClient

    const eventFormService: EventFormService<EventForm> =
      inject<EventFormService<EventForm>>("EventFormService")
    eventFormService.registerProperty({
      name: "urgent",
    })
    eventFormService.registerProperty({
      name: "reminders",
    })

    const urgent: WritableComputedRef<boolean> = computed({
      get(): boolean {
        return eventFormService.getFormValue("urgent").value || null
      },
      set(value: boolean): void {
        eventFormService.setFormValue("urgent", value)
      },
    })

    const reminders: WritableComputedRef<any[]> = computed({
      get(): any[] {
        return eventFormService.getFormValue("reminders").value || []
      },
      set(value: any[]): void {
        eventFormService.setFormValue("reminders", value)
      },
    })

    const startDate = eventFormService.getFormValue("start_date")
    const group = eventFormService.getFormValue("group")
    const levelId = computed(() => group.value?.level_id || null)

    const currentEvent = computed(() => store.getters["event/currentEvent"])

    const mailedAt = computed(() => currentEvent.value?.mailed_at || null)

    const isEditForm = eventFormService.isEdit

    const getReminderDate = (key, startDateValue = startDate.value) => {
      // Set the on creation reminder to some date in the past when editing an existing event
      // in order to handle it uniformely like the others.
      if (isEditForm && key === "on_creation") {
        return moment(currentEvent.value?.created_at)
      }
      const number = EVENT_REMINDERS[key].intervalAmount
      const type = EVENT_REMINDERS[key].intervalType
      const date = startDateValue || new Date()
      return moment(date).subtract(number, type)
    }

    const isReminderInFuture = (key, startDateValue = startDate.value) => {
      return (
        startDate &&
        getReminderDate(key, startDateValue).isSameOrAfter(moment())
      )
    }

    const alertTimeText = computed(() => {
      if (!isEditForm.value || !mailedAt.value) return ""
      return t$("components.topic_form.urgent_message_sent_at", {
        time: formatDateTime(mailedAt.value),
      })
    })

    return {
      getReminderDate,
      isReminderInFuture,
      urgent,
      reminders,
      t$,
      group,
      levelId,
      startDate,
      isEditForm,
      mailedAt,
      canUrgentDigestOnClient,
      alertTimeText,
    }
  },
})

export default ReminderFields

export const ReminderFieldsDefinition = {
  key: "notifications",
  component: ReminderFields,
  id: PanelIdentifier.REMINDER,
}
</script>

<style lang="sass"></style>
